import { useEffect, useState } from "react"
import { Card } from "@/components/ui/card"
import { adminVpsApi, adminSystemApi, type VpsStatsDTO } from "@/lib/api"

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(gb: number): string {
  if (gb < 0.001) return "0 MB"
  if (gb < 1) return `${(gb * 1024).toFixed(0)} MB`
  return `${gb.toFixed(2)} GB`
}

function formatUptime(s: number): string {
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  return d > 0 ? `${d}d ${h}h` : `${h}h ${m}m`
}

function pct(used: number, total: number): number {
  if (!total) return 0
  return Math.min(100, Math.round((used / total) * 100))
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Bar({ used, total, color, h = 6 }: { used: number; total: number; color?: string; h?: number }) {
  const p = pct(used, total)
  const bg = color ?? (p > 85 ? 'oklch(0.58 0.17 28)' : p > 65 ? 'oklch(0.65 0.15 58)' : 'oklch(0.52 0.16 262)')
  return (
    <div className="rounded-full overflow-hidden bg-secondary" style={{ height: h }}>
      <div style={{ width: `${p}%`, height: '100%', background: bg, transition: 'width 0.4s' }} />
    </div>
  )
}

const SEGMENT_COLORS = {
  system:  'oklch(0.52 0.16 262)',   // blue
  clients: 'oklch(0.62 0.17 145)',   // green
  assets:  'oklch(0.68 0.17 58)',    // amber
  temp:    'oklch(0.62 0.16 320)',   // pink
  free:    'oklch(0.78 0 0)',        // light grey — visible on dark bg
}

function StackedDiskBar({ bd, totalGb }: {
  bd: VpsStatsDTO['disk_breakdown']
  totalGb: number
}) {
  const segs = [
    { key: 'system',  label: 'System',  gb: bd.system_gb,  color: SEGMENT_COLORS.system  },
    { key: 'clients', label: 'Clients', gb: bd.clients_gb, color: SEGMENT_COLORS.clients },
    { key: 'assets',  label: 'Assets',  gb: bd.assets_gb,  color: SEGMENT_COLORS.assets  },
    { key: 'temp',    label: 'Temp',    gb: bd.temp_gb,    color: SEGMENT_COLORS.temp    },
    { key: 'free',    label: 'Free',    gb: bd.free_gb,    color: SEGMENT_COLORS.free    },
  ]
  return (
    <div>
      {/* Stacked bar */}
      <div className="flex h-6 rounded-lg overflow-hidden gap-px mb-4">
        {segs.map(s => {
          const p = pct(s.gb, totalGb)
          if (p === 0) return null
          return (
            <div
              key={s.key}
              title={`${s.label}: ${fmt(s.gb)}`}
              style={{ width: `${p}%`, background: s.color, transition: 'width 0.4s', minWidth: p > 0 ? 2 : 0 }}
            />
          )
        })}
      </div>
      {/* Legend */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
        {segs.map(s => (
          <div key={s.key} className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: s.color }} />
              <span className="text-xs text-muted-foreground">{s.label}</span>
            </div>
            <div className="text-right">
              <span className="text-xs font-semibold tabular-nums">{fmt(s.gb)}</span>
              <span className="text-[10px] text-muted-foreground ml-1">({pct(s.gb, totalGb)}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function StatCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Card className="p-5">
      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-4">{label}</div>
      {children}
    </Card>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function VPSMonitor() {
  const [stats, setStats] = useState<VpsStatsDTO | null>(null)
  const [maintenance, setMaintenance] = useState(false)
  const [togglingMaintenance, setTogglingMaintenance] = useState(false)

  useEffect(() => {
    const load = () => adminVpsApi.stats().then(setStats).catch(() => {})
    load()
    const id = setInterval(load, 10_000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    adminSystemApi.getMaintenance().then(({ enabled }) => setMaintenance(enabled)).catch(() => {})
  }, [])

  const toggleMaintenance = async () => {
    setTogglingMaintenance(true)
    try {
      const { enabled } = await adminSystemApi.setMaintenance(!maintenance)
      setMaintenance(enabled)
    } finally {
      setTogglingMaintenance(false)
    }
  }

  const cpu    = stats?.cpu_percent ?? 0
  const disk   = stats?.disk
  const bd     = stats?.disk_breakdown
  const mem    = stats?.memory
  const uptime = stats ? formatUptime(stats.uptime_seconds) : '—'
  const clients = stats?.client_storage ?? []

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">VPS Monitor</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Uptime: <strong>{uptime}</strong></p>
        </div>
        <button
          onClick={toggleMaintenance}
          disabled={togglingMaintenance}
          className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
            maintenance
              ? 'bg-amber-500/15 text-amber-500 hover:bg-amber-500/25'
              : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
          }`}
        >
          <span className={`h-2 w-2 rounded-full ${maintenance ? 'bg-amber-500' : 'bg-muted-foreground/40'}`} />
          {maintenance ? 'Maintenance ON' : 'Maintenance OFF'}
        </button>
      </div>

      {maintenance && (
        <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-4 py-3 text-sm text-amber-500">
          Maintenance mode is active — all CLI users are currently blocked.
        </div>
      )}

      {/* Top row: Disk breakdown + CPU + Memory */}
      <div className="grid grid-cols-[2fr_1fr_1fr] gap-4">

        {/* Disk */}
        <StatCard label={`Disk — ${disk ? Math.round(disk.total_gb) : '—'} GB total`}>
          {bd && disk ? (
            <StackedDiskBar bd={bd} totalGb={disk.total_gb} />
          ) : (
            <div className="h-6 rounded-lg bg-secondary animate-pulse" />
          )}
        </StatCard>

        {/* CPU */}
        <StatCard label="CPU">
          <div className="text-4xl font-bold tracking-tight mb-2">
            {Math.round(cpu)}<span className="text-xl font-normal text-muted-foreground">%</span>
          </div>
          <Bar used={cpu} total={100} h={8} />
        </StatCard>

        {/* Memory */}
        <StatCard label="Memory">
          <div className="text-4xl font-bold tracking-tight mb-2">
            {mem ? fmt(mem.used_gb) : '—'}
            <span className="text-base font-normal text-muted-foreground"> / {mem ? Math.round(mem.total_gb) : '—'} GB</span>
          </div>
          <Bar used={mem?.used_gb ?? 0} total={mem?.total_gb ?? 1} h={8} />
        </StatCard>
      </div>

      {/* Disk breakdown detail cards */}
      {bd && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'System',  gb: bd.system_gb,  desc: 'OS, apps, DB',           color: SEGMENT_COLORS.system  },
            { label: 'Assets',  gb: bd.assets_gb,  desc: 'Avatars, glasses, renders', color: SEGMENT_COLORS.assets  },
            { label: 'Temp',    gb: bd.temp_gb,    desc: 'Working & temp files',    color: SEGMENT_COLORS.temp    },
            { label: 'Free',    gb: bd.free_gb,    desc: 'Available disk space',    color: SEGMENT_COLORS.free    },
          ].map(s => (
            <Card key={s.label} className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{s.label}</span>
              </div>
              <div className="text-2xl font-bold tracking-tight">{fmt(s.gb)}</div>
              <div className="text-xs text-muted-foreground mt-1">{s.desc}</div>
            </Card>
          ))}
        </div>
      )}

      {/* Per-client storage */}
      <Card className="overflow-hidden p-0">
        <div className="px-5 py-3.5 border-b flex items-center justify-between">
          <span className="text-sm font-semibold">Storage per Client</span>
          {bd && (
            <span className="text-xs text-muted-foreground">
              {fmt(bd.clients_gb)} used across all clients
            </span>
          )}
        </div>

        {clients.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">No clients yet.</div>
        )}

        {clients.map(c => {
          const usedPct = pct(c.used_gb, c.quota_gb)
          const almostFull = usedPct > 85
          const warning = usedPct > 65
          return (
            <div key={c.tenant_id} className="flex items-center gap-4 px-5 py-3 border-b last:border-0">
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold text-muted-foreground flex-shrink-0">
                {c.name.slice(0, 2).toUpperCase()}
              </div>

              {/* Name */}
              <div className="w-32 flex-shrink-0">
                <div className="text-sm font-medium truncate">{c.name}</div>
                <div className={`text-[10px] font-semibold mt-0.5 ${almostFull ? 'text-destructive' : warning ? 'text-amber-400' : 'text-muted-foreground'}`}>
                  {usedPct}% used
                </div>
              </div>

              {/* Bar */}
              <div className="flex-1">
                <Bar
                  used={c.used_gb}
                  total={c.quota_gb}
                  color={almostFull ? 'oklch(0.58 0.17 28)' : warning ? 'oklch(0.65 0.15 58)' : SEGMENT_COLORS.clients}
                  h={5}
                />
              </div>

              {/* Numbers */}
              <div className="text-xs text-muted-foreground w-32 text-right flex-shrink-0 tabular-nums">
                {fmt(c.used_gb)} <span className="text-muted-foreground/50">/</span> {fmt(c.quota_gb)}
              </div>
            </div>
          )
        })}
      </Card>
    </div>
  )
}
